/**
 * PostHog Provider Component
 * Ensures PostHog is properly initialized and provides tracking context.
 * PostHog + Session Replay apply to ALL templates (BaseTemplate, BioLinkTemplate, ArgentinaBioTemplate).
 */

import React from 'react';

interface PostHogProviderProps {
  children: React.ReactNode;
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  // Simple provider that just renders children
  // PostHog initialization is handled globally in main.tsx
  return <>{children}</>;
};

// Safe PostHog hook with error handling
export const usePostHog = () => {
  const [status, setStatus] = React.useState({
    isInitialized: false,
    isRecording: false,
    distinctId: null,
    sessionId: null
  });

  React.useEffect(() => {
    try {
      const checkStatus = () => {
        if (typeof window !== 'undefined' && window.posthog) {
          setStatus({
            isInitialized: !!(window.posthog as any).__loaded,
            isRecording: (window.posthog as any).sessionRecordingStarted?.() || false,
            distinctId: (window.posthog as any).get_distinct_id?.() || null,
            sessionId: (window.posthog as any).get_session_id?.() || null
          });
        }
      };

      checkStatus();
      const interval = setInterval(checkStatus, 2000); // Check every 2 seconds

      return () => clearInterval(interval);
    } catch (error) {
    }
  }, []);

  return {
    ...status,
    startRecording: () => {
      try {
        if ((window.posthog as any)?.startSessionRecording) {
          (window.posthog as any).startSessionRecording(true);
        }
      } catch (error) {
      }
    },
    stopRecording: () => {
      try {
        if ((window.posthog as any)?.stopSessionRecording) {
          (window.posthog as any).stopSessionRecording();
        }
      } catch (error) {
      }
    },
    debugStatus: () => {
      try {
        if (window.posthog && import.meta.env.DEV) {
          console.log("PostHog status", {
            loaded: !!(window.posthog as any).__loaded,
            distinctId: (window.posthog as any).get_distinct_id?.(),
            sessionId: (window.posthog as any).get_session_id?.(),
            recording: (window.posthog as any).sessionRecordingStarted?.(),
          });
        }
      } catch (error) {
      }
    }
  };
};

export const withPostHogTracking = (Component: React.ComponentType) => Component;

export const usePostHogFeatureFlag = (flag: string) => {
  try {
    return (window.posthog as any)?.getFeatureFlag?.(flag) || false;
  } catch (error) {
    return false;
  }
};

export const usePostHogTracking = () => ({
  trackTemplateUsage: (template: string, data?: any) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('template_usage', { template, ...data });
      }
    } catch (error) {
    }
  },
  trackButtonClick: (buttonId: string, data?: any) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('button_click', { button_id: buttonId, ...data });
      }
    } catch (error) {
    }
  },
  trackSocialClick: (platform: string, data?: any) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('social_click', { platform, ...data });
      }
    } catch (error) {
    }
  },
  trackConversion: (type: string, data?: any) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('conversion', { conversion_type: type, ...data });
      }
    } catch (error) {
    }
  },
  trackEnhancedButtonClick: (buttonData: any) => {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture('enhanced_button_click', buttonData);
      }
    } catch (error) {
    }
  }
});

export default PostHogProvider;